import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { Alert, Button, Card, Empty, Select, Spin } from "antd";
import axios from "axios";
import {
  ShoppingOutlined,
  ShopOutlined,
  TeamOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import axiosInstance from "api/axiosInstance";
import { API_ENDPOINTS } from "api/endpoints";
import CmsTopBar from "components/cmslayout/CmsTopBar";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { fetchMarkets } from "store/slices/marketSlice";

interface DashboardProps {}

type MarketSelectValue = "all" | number;

interface MarketsRevenueMonth {
  month: number;
  label: string;
  revenue: number;
}

interface MarketsRevenueData {
  year: number;
  market_id: number | null;
  market_name: string;
  total_revenue: number;
  months: MarketsRevenueMonth[];
}

interface MarketsRevenueResponse {
  success: boolean;
  message: string;
  data: MarketsRevenueData;
}

interface DashboardSummaryData {
  total_markets: number;
  approved_stores: number;
  pending_stores: number;
  total_income: number;
  commission_percent: number;
}

interface DashboardSummaryResponse {
  success: boolean;
  message: string;
  data: DashboardSummaryData;
}

interface ChartPoint {
  month: string;
  revenue: number;
}

const kpiCards = [
  {
    key: "markets",
    label: "Total Markets",
    icon: <ShoppingOutlined />,
    iconBg: "#E3F2FD",
    iconColor: "#1976D2",
    getValue: (data: DashboardSummaryData) => `${data.total_markets}`,
  },
  {
    key: "store",
    label: "Approved Store",
    icon: <ShopOutlined />,
    iconBg: "#FFF8E1",
    iconColor: "#F9A825",
    getValue: (data: DashboardSummaryData) => `${data.approved_stores}`,
  },
  {
    key: "vender",
    label: "Pending Store",
    icon: <TeamOutlined />,
    iconBg: "#FFE0B2",
    iconColor: "#EF6C00",
    getValue: (data: DashboardSummaryData) => `${data.pending_stores}`,
  },
  {
    key: "income",
    label: "Total Income",
    icon: <DollarOutlined />,
    iconBg: "#E8F5E9",
    iconColor: "#2E7D32",
    getValue: (data: DashboardSummaryData) => formatNaira(data.total_income, 2),
    getMeta: (data: DashboardSummaryData) =>
      `Commission: ${data.commission_percent}%`,
  },
];

const yearOptions = [
  { value: 2024, label: "Yearly 2024" },
  { value: 2025, label: "Yearly 2025" },
  { value: 2026, label: "Yearly 2026" },
];

const formatNaira = (value: number, fractionDigits = 0) =>
  new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(value);

const formatYAxisValue = (value: number) => {
  if (value >= 1000000) return `₦${(value / 1000000).toFixed(1)}m`;
  if (value >= 1000) return `₦${Math.round(value / 1000)}k`;
  return `₦${value}`;
};

export const Dashboard: React.FunctionComponent<DashboardProps> = () => {
  const dispatch = useAppDispatch();
  const { loading: marketsLoading, markets } = useAppSelector(
    (state) => state.markets
  );
  const [selectedMarket, setSelectedMarket] = useState<MarketSelectValue>("all");
  const [selectedYear, setSelectedYear] = useState(2026);
  const [summaryData, setSummaryData] = useState<DashboardSummaryData | null>(
    null
  );
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [revenueData, setRevenueData] = useState<MarketsRevenueData | null>(null);
  const [revenueLoading, setRevenueLoading] = useState(false);
  const [revenueError, setRevenueError] = useState<string | null>(null);

  useEffect(() => {
    if (markets.length === 0 && !marketsLoading) {
      dispatch(fetchMarkets());
    }
  }, [dispatch, markets.length, marketsLoading]);

  const loadDashboardSummary = useCallback(async (signal?: AbortSignal) => {
    setSummaryLoading(true);
    setSummaryError(null);
    setSummaryData(null);

    try {
      const response = await axiosInstance.get<DashboardSummaryResponse>(
        API_ENDPOINTS.dashboard.adminSummary,
        { signal }
      );

      if (!response.data.success) {
        throw new Error(
          response.data.message || "Unable to load dashboard summary."
        );
      }

      setSummaryData(response.data.data);
    } catch (error) {
      if (axios.isCancel(error) || (error as Error).name === "CanceledError") {
        return;
      }

      setSummaryError("Unable to load dashboard summary.");
      setSummaryData(null);
    } finally {
      if (!signal?.aborted) {
        setSummaryLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    loadDashboardSummary(controller.signal);

    return () => controller.abort();
  }, [loadDashboardSummary]);

  const loadMarketRevenue = useCallback(
    async (signal?: AbortSignal) => {
      setRevenueLoading(true);
      setRevenueError(null);
      setRevenueData(null);

      try {
        const params: { year: number; market_id?: number } = {
          year: selectedYear,
        };

        if (selectedMarket !== "all") {
          params.market_id = selectedMarket;
        }

        const response = await axiosInstance.get<MarketsRevenueResponse>(
          API_ENDPOINTS.dashboard.adminMarketsRevenue,
          { params, signal }
        );

        if (!response.data.success) {
          throw new Error(
            response.data.message || "Unable to load market revenue."
          );
        }

        setRevenueData(response.data.data);
      } catch (error) {
        if (axios.isCancel(error) || (error as Error).name === "CanceledError") {
          return;
        }

        setRevenueError("Unable to load market revenue.");
        setRevenueData(null);
      } finally {
        if (!signal?.aborted) {
          setRevenueLoading(false);
        }
      }
    },
    [selectedMarket, selectedYear]
  );

  useEffect(() => {
    const controller = new AbortController();
    loadMarketRevenue(controller.signal);

    return () => controller.abort();
  }, [loadMarketRevenue]);

  const marketOptions = useMemo(
    () => [
      { value: "all" as const, label: "All Markets" },
      ...markets.map((market) => ({
        value: market.id,
        label: market.name,
      })),
    ],
    [markets]
  );

  const chartData = useMemo<ChartPoint[]>(() => {
    return (
      revenueData?.months.map((item) => ({
        month: item.label,
        revenue: item.revenue,
      })) || []
    );
  }, [revenueData]);

  const hasOnlyZeroRevenue =
    chartData.length > 0 && chartData.every((item) => item.revenue === 0);

  const chartMax = Math.max(...chartData.map((item) => item.revenue), 0);
  const yAxisMax =
    chartMax > 0 ? Math.ceil((chartMax * 1.15) / 1000) * 1000 : 1000;

  return (
    <React.Fragment>
      <CmsTopBar title="Main Dashboard" />

      <Container fluid className="dashboard-wrap">
        <Container fluid className="px-3 py-4">
          {summaryError && (
            <Alert
              className="dashboard-summary-error mb-3"
              message="Unable to load dashboard summary."
              type="error"
              showIcon
            />
          )}

          <Row className="g-3 mb-4">
            {kpiCards.map((card) => (
              <Col key={card.key} xs={12} sm={12} md={6} lg={6} xl={3}>
                <Card className="dashboard-kpi-card" bordered={false}>
                  <div className="dashboard-kpi-row">
                    <div
                      className="dashboard-kpi-icon-wrap"
                      style={{ backgroundColor: card.iconBg, color: card.iconColor }}
                    >
                      {card.icon}
                    </div>
                    <div className="dashboard-kpi-text">
                      <div className="dashboard-kpi-value">
                        {summaryLoading || !summaryData
                          ? "--"
                          : card.getValue(summaryData)}
                      </div>
                      <div className="dashboard-kpi-label">{card.label}</div>
                      {!summaryLoading && summaryData && card.getMeta && (
                        <div className="dashboard-kpi-meta">
                          {card.getMeta(summaryData)}
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              </Col>
            ))}
          </Row>

          <Row>
            <Col xs={12}>
              <Card className="dashboard-chart-card" bordered={false}>
                <div className="dashboard-chart-header d-flex justify-content-between align-items-start flex-wrap gap-3">
                  <div>
                    <div className="dashboard-chart-title-wrap d-flex align-items-center gap-2 flex-wrap">
                      <span className="dashboard-chart-title">Markets Revenue</span>

                      <Select
                        value={selectedMarket}
                        size="small"
                        bordered={false}
                        className="dashboard-chart-select"
                        options={marketOptions}
                        onChange={(value) => setSelectedMarket(value)}
                        loading={marketsLoading}
                        suffixIcon={<span style={{ fontSize: 10 }}>▼</span>}
                      />
                    </div>

                    <div className="dashboard-chart-big-value">
                      {formatNaira(revenueData?.total_revenue || 0)}
                    </div>
                    {revenueData?.market_name && (
                      <div className="dashboard-chart-market-name">
                        {revenueData.market_name}
                      </div>
                    )}
                  </div>

                  <Select
                    value={selectedYear}
                    size="small"
                    bordered={false}
                    className="dashboard-chart-select dashboard-chart-year-select"
                    options={yearOptions}
                    onChange={(value) => setSelectedYear(value)}
                    suffixIcon={<span style={{ fontSize: 10 }}>▼</span>}
                  />
                </div>

                <div className="dashboard-chart-inner">
                  {revenueLoading ? (
                    <div className="dashboard-chart-state">
                      <Spin />
                    </div>
                  ) : revenueError ? (
                    <div className="dashboard-chart-state">
                      <div className="dashboard-chart-error">
                        <span>Unable to load market revenue.</span>
                        <Button size="small" onClick={() => loadMarketRevenue()}>
                          Retry
                        </Button>
                      </div>
                    </div>
                  ) : chartData.length === 0 ? (
                    <div className="dashboard-chart-state">
                      <Empty description="No revenue data available for selected market/year." />
                    </div>
                  ) : (
                    <>
                      {hasOnlyZeroRevenue && (
                        <div className="dashboard-chart-empty-note">
                          No revenue data available for selected market/year.
                        </div>
                      )}
                      <ResponsiveContainer width="100%" height={320}>
                        <AreaChart
                          data={chartData}
                          margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                        >
                          <defs>
                            <linearGradient
                              id="colorValue"
                              x1="0"
                              y1="0"
                              x2="0"
                              y2="1"
                            >
                              <stop
                                offset="5%"
                                stopColor="#00C2E0"
                                stopOpacity={0.4}
                              />
                              <stop
                                offset="95%"
                                stopColor="#00C2E0"
                                stopOpacity={0}
                              />
                            </linearGradient>
                          </defs>

                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#f0f0f0"
                            vertical={false}
                          />

                          <XAxis
                            dataKey="month"
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: "#999", fontSize: 12 }}
                          />

                          <YAxis
                            axisLine={false}
                            tickLine={false}
                            tick={{ fill: "#999", fontSize: 12 }}
                            tickFormatter={(value) => formatYAxisValue(Number(value))}
                            domain={[0, yAxisMax]}
                          />

                          <Tooltip
                            formatter={(value) => [
                              formatNaira(Number(value)),
                              "Revenue",
                            ]}
                            contentStyle={{
                              borderRadius: 12,
                              border: "1px solid #f0f0f0",
                              boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                            }}
                          />

                          <Area
                            type="monotone"
                            dataKey="revenue"
                            stroke="#00C2E0"
                            strokeWidth={4}
                            fill="url(#colorValue)"
                            dot={false}
                            activeDot={{
                              r: 8,
                              fill: "#00C2E0",
                              stroke: "#fff",
                              strokeWidth: 3,
                            }}
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </>
                  )}
                </div>
              </Card>
            </Col>
          </Row>
        </Container>
      </Container>
    </React.Fragment>
  );
};

export default Dashboard;
